import { computed, ComputedRef } from '@vue/composition-api'
import { useConfirmation } from '@/modules/kisweui/compositions'
import { KeyDict } from '@/types'
import { TeamRole, AbilityRole, abilitiesToTeamRole } from '@/abilitiesByRole'
import roleNames from '@/roleNames'
import { TeamRoleItem } from '@/modules/teams/types'
import { sortBy } from 'lodash'
import store from '@/store'
import { useAddLog } from '@/modules/logging/compositions'

const { addLog, addLogError } = useAddLog()

const addTeamRole = async (role: TeamRole) => {
  const result = await store.dispatch.abilities.addRole(role)
  if (result.isSuccess) {
    addLog(`${ role.name } is added`, 'info', { role }).catch((error) => console.error(error))
  } else {
    addLogError(`Could not add ${ role.name }`, result.error, { role }).catch((error) => console.error(error))
  }
}

const editTeamRole = async (id: string, role: TeamRole) => {
  const result = await store.dispatch.abilities.updateRole({ id, role })
  if (result.isSuccess) {
    addLog(`${ role.name } is updated`, 'info', { id, role }).catch((error) => console.error(error))
  } else {
    addLogError(`Could not update ${ role.name }`, result.error, { id, role }).catch((error) => console.error(error))
  }
}

const deleteTeamRole = async (roleId: string) => {
  const result = await store.dispatch.abilities.deleteRole(roleId)
  if (result.isSuccess) {
    addLog(`${roleId} is deleted`, 'info', { roleId }).catch((error) => console.error(error))
  } else {
    addLogError(`Could not delete ${ roleId }`, result.error, { roleId }).catch((error) => console.error(error))
  }
}

const isStandardRole = (roleId: string) => {
  return Object.values(AbilityRole).includes(roleId as AbilityRole)
}

const getRoleItems = (roles: KeyDict<TeamRole>): TeamRoleItem[] => {
  const roleItems = Object.entries(roles).reduce(
      (allRoles: TeamRoleItem[], [roleId, role]: [roleId: string, role: TeamRole]) => {
    if (roleId === AbilityRole.SuperUser || roleId === AbilityRole.CrewObserver) return allRoles
    const isPredefinedRole = Object.values(AbilityRole).includes(roleId as AbilityRole)
    const name = isPredefinedRole ? roleNames(roleId) : role.name
    allRoles.push({ ...role, id: roleId, name })
    return allRoles
  }, [])
  return sortBy(roleItems, 'name')
}

const useTeamRoles = (customRoles: ComputedRef<KeyDict<TeamRole>>) => {
  const { askConfirmation } = useConfirmation()
  const activeTeam = computed(() => store.state.team.activeTeam)
  const initialTeamRole = computed(() => ({
    id: '', team_id: activeTeam.value, name: '', description: '', abilities: {}, deleted: false
  } as TeamRoleItem))

  const standardRoles = computed(() => {
    return Object.values(AbilityRole).reduce((allRoles: KeyDict<TeamRole>, currentRole: AbilityRole) => {
      allRoles[currentRole] = abilitiesToTeamRole(currentRole)
      allRoles[currentRole].name = roleNames(currentRole)
      return allRoles
    }, {})
  })

  const askDeleteTeamRole = async (roleId: string) => {
    await askConfirmation({
      question: 'Are you sure you want to delete this Team Role?',
      action: async () => await deleteTeamRole(roleId),
      title: 'Delete role',
      options: { confirmButtonText: 'Delete' }
    })
  }

  const requestTeamRoleAction = async (role: TeamRole, selectedTeamId: string) => {
    if (!isStandardRole(selectedTeamId)) {
      if (selectedTeamId === '') {
        await addTeamRole(role)
      } else {
        await editTeamRole(selectedTeamId, role)
      }
    }
  }

  return {
    askDeleteTeamRole,
    allRoles: computed(() => getRoleItems({ ...standardRoles.value, ...customRoles.value })),
    customRoles,
    initialTeamRole,
    isStandardRole,
    requestTeamRoleAction
  }
}
export default useTeamRoles
